import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, Users, CheckCircle, TrendingUp, BarChart3, Target } from "lucide-react";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from "recharts";

const ORG_COLORS = ["#4A7AB5", "#F5A623", "#5BA88E", "#E06C75", "#C678DD", "#61DAFB", "#98C379"];
const DIM_LABELS = {
    ego_learning: "Ego vs Learning",
    authority_feedback: "Authority & Feedback",
    ownership: "Ownership",
    cognitive_flexibility: "Cognitive Flex",
    behavioral_followthrough: "Follow-through",
    reflection_depth: "Reflection"
};

export default function OrgReports() {
    const [data, setData] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/org-reports")
            .then(res => setData(res.data.organizations || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const totals = useMemo(() => {
        const totalUsers = data.reduce((s, o) => s + o.total_users, 0);
        const totalCompleted = data.reduce((s, o) => s + o.completed, 0);
        const scores = data.filter(o => o.avg_score > 0).map(o => o.avg_score);
        const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
        return { totalUsers, totalCompleted, avgScore, orgCount: data.length };
    }, [data]);

    const completionData = useMemo(() =>
        data.map(o => ({
            name: o.organization.length > 18 ? o.organization.substring(0, 18) + "..." : o.organization,
            fullName: o.organization,
            completed: o.completed,
            pending: o.pending,
            rate: o.completion_rate
        })),
    [data]);

    const scoreData = useMemo(() =>
        data.filter(o => o.avg_score > 0).map(o => ({
            name: o.organization.length > 18 ? o.organization.substring(0, 18) + "..." : o.organization,
            fullName: o.organization,
            avg: o.avg_score,
            min: o.min_score,
            max: o.max_score
        })),
    [data]);

    const radarData = useMemo(() => {
        const orgsWithDims = data.filter(o => Object.keys(o.dimension_averages).length > 0);
        if (orgsWithDims.length === 0) return [];
        const allDims = Object.keys(DIM_LABELS);
        return allDims.map(dk => {
            const entry = { dimension: DIM_LABELS[dk] || dk };
            orgsWithDims.forEach(o => {
                entry[o.organization] = o.dimension_averages[dk] || 0;
            });
            return entry;
        });
    }, [data]);

    const radarOrgs = useMemo(() =>
        data.filter(o => Object.keys(o.dimension_averages).length > 0).map(o => o.organization),
    [data]);

    const selectedOrgData = useMemo(() =>
        selectedOrg !== "all" ? data.find(o => o.organization === selectedOrg) : null,
    [data, selectedOrg]);

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    return (
        <div data-testid="org-reports" className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-foreground" style={{ fontFamily: "Outfit" }}>
                        Organization Reports
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Aggregated coachability insights across organizations</p>
                </div>
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                    <SelectTrigger data-testid="org-selector" className="w-full sm:w-[220px] h-9 bg-background border-border text-sm">
                        <SelectValue placeholder="Select Organization" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Organizations</SelectItem>
                        {data.map(o => (
                            <SelectItem key={o.organization} value={o.organization}>{o.organization}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Summary Stats */}
            {!selectedOrgData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { label: "Organizations", value: totals.orgCount, icon: Building2, color: "#4A7AB5" },
                        { label: "Total Users", value: totals.totalUsers, icon: Users, color: "#F5A623" },
                        { label: "Completed", value: totals.totalCompleted, icon: CheckCircle, color: "#5BA88E" },
                        { label: "Avg. Score", value: totals.avgScore, icon: TrendingUp, color: "#8CAED6" },
                    ].map((s, i) => (
                        <Card key={s.label} className="border-border bg-card">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg" style={{ backgroundColor: s.color + "15" }}>
                                        <s.icon className="h-5 w-5" style={{ color: s.color }} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">{s.label}</p>
                                        <p className="text-xl font-bold text-foreground" data-testid={`org-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>{s.value}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Single Org Detail View */}
            {selectedOrgData && (
                <OrgDetail org={selectedOrgData} />
            )}

            {/* All Orgs - Comparison Charts */}
            {!selectedOrgData && (
                <>
                    {/* Completion Rate Chart */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        <Card className="border-border bg-card">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                                    Completion by Organization
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={completionData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                            <XAxis type="number" stroke="#64748B" fontSize={12} />
                                            <YAxis type="category" dataKey="name" width={140} stroke="#64748B" fontSize={11} tick={{ fill: "#64748B" }} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#1E293B", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                                                formatter={(val, name) => [val, name === "completed" ? "Completed" : "Pending"]}
                                            />
                                            <Bar dataKey="completed" fill="#5BA88E" stackId="a" radius={[0, 0, 0, 0]} name="Completed" />
                                            <Bar dataKey="pending" fill="#E2E8F0" stackId="a" radius={[0, 4, 4, 0]} name="Pending" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Avg Score Comparison */}
                        <Card className="border-border bg-card">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                                    Average Score by Organization
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={scoreData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                            <XAxis type="number" domain={[0, 100]} stroke="#64748B" fontSize={12} />
                                            <YAxis type="category" dataKey="name" width={140} stroke="#64748B" fontSize={11} tick={{ fill: "#64748B" }} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#1E293B", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                                                formatter={(val, name, props) => {
                                                    const p = props.payload;
                                                    return [<span key="v">{val} <span style={{color:"#94A3B8",fontSize:11}}>(min: {p.min}, max: {p.max})</span></span>, p.fullName];
                                                }}
                                            />
                                            <Bar dataKey="avg" radius={[0, 4, 4, 0]}>
                                                {scoreData.map((entry, i) => (
                                                    <Cell key={`score-${entry.fullName}`} fill={ORG_COLORS[i % ORG_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Dimension Radar Comparison */}
                    {radarData.length > 0 && (
                        <Card className="border-border bg-card">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                                    Dimension Comparison Across Organizations
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RadarChart data={radarData}>
                                            <PolarGrid stroke="#E2E8F0" />
                                            <PolarAngleAxis dataKey="dimension" tick={{ fill: "#64748B", fontSize: 11 }} />
                                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 10 }} />
                                            {radarOrgs.map((org, i) => (
                                                <Radar key={org} name={org} dataKey={org} stroke={ORG_COLORS[i % ORG_COLORS.length]}
                                                    fill={ORG_COLORS[i % ORG_COLORS.length]} fillOpacity={0.1} strokeWidth={2} />
                                            ))}
                                            <Legend wrapperStyle={{ fontSize: 12, color: "#64748B" }} />
                                            <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#1E293B", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Organization Table */}
                    <Card className="border-border bg-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                                Organization Summary
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-border">
                                            <TableHead className="text-muted-foreground">Organization</TableHead>
                                            <TableHead className="text-muted-foreground">Users</TableHead>
                                            <TableHead className="text-muted-foreground">Completed</TableHead>
                                            <TableHead className="text-muted-foreground">Completion %</TableHead>
                                            <TableHead className="text-muted-foreground">Avg Score</TableHead>
                                            <TableHead className="text-muted-foreground">Score Range</TableHead>
                                            <TableHead className="text-muted-foreground">Departments</TableHead>
                                            <TableHead className="text-muted-foreground">Top Archetype</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.map((o, i) => {
                                            const topArchetype = Object.entries(o.archetype_distribution).sort((a, b) => b[1] - a[1])[0];
                                            return (
                                                <TableRow key={o.organization} className="border-border hover:bg-secondary/50 cursor-pointer"
                                                    onClick={() => setSelectedOrg(o.organization)} data-testid={`org-row-${i}`}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ORG_COLORS[i % ORG_COLORS.length] }} />
                                                            <span className="font-medium text-foreground text-sm">{o.organization}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-foreground font-medium">{o.total_users}</TableCell>
                                                    <TableCell className="text-sm text-foreground">{o.completed}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 bg-secondary rounded-full h-1.5">
                                                                <div className="h-1.5 rounded-full" style={{ width: `${o.completion_rate}%`, backgroundColor: "#5BA88E" }} />
                                                            </div>
                                                            <span className="text-xs text-muted-foreground">{o.completion_rate}%</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-sm font-medium text-foreground">{o.avg_score || "-"}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {o.min_score && o.max_score ? `${o.min_score} - ${o.max_score}` : "-"}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">{o.departments.join(", ") || "-"}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                                                        {topArchetype ? topArchetype[0] : "-"}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}

function OrgDetail({ org }) {
    const dimData = Object.entries(org.dimension_averages).map(([k, v]) => ({
        dimension: DIM_LABELS[k] || k,
        score: v
    }));
    const DIM_COLORS = ["#5B9BD5", "#F4A261", "#E06C75", "#61DAFB", "#98C379", "#C678DD"];
    const archetypeData = Object.entries(org.archetype_distribution).map(([name, count]) => ({
        name: name.length > 25 ? name.substring(0, 25) + "..." : name,
        fullName: name, value: count
    }));

    return (
        <div data-testid="org-detail" className="space-y-6">
            {/* Header Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                    { label: "Total Users", value: org.total_users, icon: Users, color: "#F5A623" },
                    { label: "Completed", value: org.completed, icon: CheckCircle, color: "#5BA88E" },
                    { label: "Completion Rate", value: `${org.completion_rate}%`, icon: Target, color: "#4A7AB5" },
                    { label: "Avg Score", value: org.avg_score, icon: TrendingUp, color: "#8CAED6" },
                    { label: "Score Range", value: org.min_score ? `${org.min_score}-${org.max_score}` : "-", icon: BarChart3, color: "#C678DD" },
                ].map((s, i) => (
                    <Card key={s.label} className="border-border bg-card">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: s.color + "15" }}>
                                    <s.icon className="h-5 w-5" style={{ color: s.color }} />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">{s.label}</p>
                                    <p className="text-lg font-bold text-foreground">{s.value}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Dimension Averages */}
                {dimData.length > 0 && (
                    <Card className="border-border bg-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                                Average Dimension Scores
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dimData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                        <XAxis type="number" domain={[0, 100]} stroke="#64748B" fontSize={12} />
                                        <YAxis type="category" dataKey="dimension" width={120} stroke="#64748B" fontSize={11} tick={{ fill: "#64748B" }} />
                                        <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#1E293B", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
                                        <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                                            {dimData.map((entry, i) => (
                                                <Cell key={`dim-${entry.dimension}`} fill={DIM_COLORS[i % DIM_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Archetype Distribution */}
                {archetypeData.length > 0 && (
                    <Card className="border-border bg-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>
                                Archetype Distribution
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={archetypeData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                        <XAxis type="number" stroke="#64748B" fontSize={12} />
                                        <YAxis type="category" dataKey="name" width={180} stroke="#64748B" fontSize={11} tick={{ fill: "#64748B" }} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "8px", color: "#1E293B", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                                            formatter={(val, name, props) => [val, props.payload.fullName]}
                                        />
                                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                            {archetypeData.map((entry, i) => (
                                                <Cell key={`arch-${entry.name}`} fill={ORG_COLORS[i % ORG_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Departments */}
            {org.departments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-muted-foreground mr-2">Departments:</span>
                    {org.departments.map(d => (
                        <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                    ))}
                </div>
            )}
        </div>
    );
}
